/**
 * LegBuilder — read-only display of multi-leg options structure from the DSL.
 *
 * Shows each leg as a badge row: instrument, side (BUY=green/SELL=red),
 * strike offset, expiry. Also shows combo_type label and net position type.
 */

const SIDE_CLS = { BUY: "text-emerald-400", SELL: "text-rose-400" };
const INSTRUMENT_CLS = { CE: "text-sky-400", PE: "text-amber-400" };
const OFFSET_LABEL = (o) => {
  if (o === 0) return "ATM";
  if (o > 0) return `OTM${o}`;
  return `ITM${Math.abs(o)}`;
};

const COMBO_LABELS = {
  bull_call_spread:  "Bull Call Spread",
  bear_put_spread:   "Bear Put Spread",
  bull_put_spread:   "Bull Put Spread",
  bear_call_spread:  "Bear Call Spread",
  long_straddle:     "Long Straddle",
  short_straddle:    "Short Straddle",
  long_strangle:     "Long Strangle",
  short_strangle:    "Short Strangle",
  iron_condor:       "Iron Condor",
  jade_lizard:       "Jade Lizard",
  collar:            "Collar",
  custom:            "Custom Combo",
};

function netType(legs) {
  const netSign = legs.reduce((acc, l) => acc + (l.side === "BUY" ? 1 : -1) * (l.qty || 1), 0);
  if (netSign > 0) return { label: "NET DEBIT", cls: "text-rose-400" };
  if (netSign < 0) return { label: "NET CREDIT", cls: "text-emerald-400" };
  return { label: "ZERO COST", cls: "txt-muted" };
}

export default function LegBuilder({ dsl }) {
  const legs = dsl?.legs;
  if (!legs || legs.length === 0) return null;

  const comboLabel = COMBO_LABELS[dsl.combo_type] || dsl.combo_type || "Options Combo";
  const net = netType(legs);

  return (
    <div className="mt-3 border border-sky-500/20 bg-sky-500/5 p-3 space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-section text-sky-400 tracking-widest">MULTI-LEG</span>
          <span className="text-xs text-white font-medium">{comboLabel}</span>
        </div>
        <span className={`text-[10px] font-section ${net.cls}`}>{net.label}</span>
      </div>

      {/* Leg rows */}
      <div className="space-y-1">
        {legs.map((leg, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-4 text-center txt-muted font-mono">{i + 1}</span>
            <span className={`font-section text-[11px] w-8 ${SIDE_CLS[leg.side] || "txt-muted"}`}>
              {leg.side}
            </span>
            <span className={`font-mono font-bold w-6 ${INSTRUMENT_CLS[leg.instrument] || "txt-muted"}`}>
              {leg.instrument}
            </span>
            <span className="txt-secondary w-10">{OFFSET_LABEL(leg.strike_offset ?? 0)}</span>
            <span className="txt-muted capitalize text-[10px]">{leg.expiry || "weekly"}</span>
            {(leg.qty || 1) > 1 && (
              <span className="txt-muted text-[10px]">×{leg.qty}</span>
            )}
          </div>
        ))}
      </div>

      <div className="text-[10px] txt-muted border-t border-[var(--border)] pt-2">
        Strikes resolved at signal time · BSM pricing · Weekly expiry default
      </div>
    </div>
  );
}
